import { ChevronDownIcon } from "@heroicons/react/20/solid";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

/**
 * Country dial code + local number, as one control — the same selector the
 * customer portal uses. The country list is the platform's own
 * (`/v1/countries`), passed in by the form that already loaded it. The number
 * is sent as typed together with the chosen country; the server normalises it
 * to E.164 and is the only judge of whether it is valid.
 *
 * The whole control is `dir="ltr"` on purpose: a phone number reads left to
 * right in Arabic too.
 */
export default function PhoneInput({
  countries,
  country,
  onCountryChange,
  id,
  invalid,
  className = "",
  ...rest
}) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const rootRef = useRef(null);

  const selected = countries.find((item) => item.iso_code === country);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase().replace(/^\+/, "");
    if (!needle) return countries;
    return countries.filter(
      (item) =>
        String(item.name ?? "")
          .toLowerCase()
          .includes(needle) ||
        item.iso_code.toLowerCase().includes(needle) ||
        String(item.phone_code ?? "")
          .replace("+", "")
          .startsWith(needle)
    );
  }, [countries, search]);

  // Outside click and Escape close the list.
  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    const onKey = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const choose = (iso) => {
    onCountryChange(iso);
    setOpen(false);
    setSearch("");
  };

  const border = invalid ? "border-danger" : "";

  return (
    <div ref={rootRef} dir="ltr" className={`relative flex gap-2 ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t("phone.country")}
        className={`field-input flex !w-auto flex-none items-center gap-1.5 whitespace-nowrap tabular-nums ${border}`}
      >
        <span aria-hidden="true">{selected?.flag || country}</span>
        <span>{selected?.phone_code ?? ""}</span>
        <ChevronDownIcon className="size-4 shrink-0 text-ink-2" aria-hidden="true" />
      </button>

      <input
        id={id}
        type="tel"
        inputMode="tel"
        autoComplete="tel-national"
        dir="ltr"
        aria-invalid={invalid || undefined}
        className={`field-input min-w-0 flex-1 text-left ${border}`}
        {...rest}
      />

      {open && (
        <div className="absolute inset-x-0 top-full z-40 mt-2 overflow-hidden rounded-lg border border-line bg-raised shadow-e2">
          <div className="p-2" dir={i18n.dir()}>
            <input
              type="search"
              autoFocus
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={t("phone.search")}
              aria-label={t("phone.search")}
              className="field-input"
            />
          </div>
          <ul role="listbox" aria-label={t("phone.country")} className="max-h-64 overflow-y-auto p-1.5">
            {filtered.map((item) => (
              <li key={item.iso_code} role="option" aria-selected={item.iso_code === country}>
                <button
                  type="button"
                  onClick={() => choose(item.iso_code)}
                  className={`flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-sm hover:bg-surface ${
                    item.iso_code === country ? "bg-surface font-semibold" : ""
                  }`}
                >
                  <span aria-hidden="true">{item.flag}</span>
                  <span dir="auto" className="flex-1 truncate text-start">
                    {item.name}
                  </span>
                  <span className="tabular-nums text-ink-2">{item.phone_code}</span>
                </button>
              </li>
            ))}
            {filtered.length === 0 && (
              <li className="px-2.5 py-2 text-sm text-ink-2" dir={i18n.dir()}>
                {t("phone.noMatch")}
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
